import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Mail, Send } from "lucide-react";
import { api, fileUrl, euro } from "../lib/api";
import { OfferBadge, OfferNote } from "./OfferBadge";
import { t } from "../i18n";

const inputCls = "w-full rounded-[20px] border-4 border-[#38B6FF]/25 bg-white px-5 py-3 text-lg outline-none focus:border-[#38B6FF]";

export const PacksShop = () => {
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/packs").then((r) => setPacks(r.data)); }, []);

  const buy = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/payments/checkout", { pack_id: id, origin_url: window.location.origin });
      toast(t("shop.redirecting"));
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setBusy(null);
    }
  };

  return (
    <section className="cloud-card mt-8 p-6 sm:p-8" id="packs" data-testid="parents-packs-section">
      <h2 className="flex items-center gap-2 text-2xl font-extrabold"><ShoppingBag className="w-7 h-7 text-[#8A3FE0]" /> {t("shop.title")}</h2>
      <p className="mt-1 text-sm opacity-75">{t("shop.adultsOnly")} <Link to="/cgv" className="underline">{t("legal.cgv")}</Link></p>
      {packs.length === 0 && <p className="mt-4 opacity-70" data-testid="packs-empty">{t("shop.empty")}</p>}
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {packs.map((p) => (
          <li key={p.id} className="flex gap-4 rounded-[24px] bg-white p-3" data-testid={`shop-pack-${p.id}`}>
            <img src={fileUrl(`/files/packs/${p.id}/preview`)} alt="" className="h-24 w-20 shrink-0 rounded-xl object-contain" />
            <div className="flex min-w-0 flex-1 flex-col">
              {p.offer && <OfferBadge offer={p.offer} testId={`shop-pack-offer-badge-${p.id}`} className="mb-2 self-start" />}
              <p className="font-bold leading-tight">{p.title}</p>
              {p.description && <p className="mt-1 text-sm opacity-75" data-testid={`shop-pack-description-${p.id}`}>{p.description}</p>}
              {p.offer && <OfferNote offer={p.offer} testId={`shop-pack-offer-note-${p.id}`} />}
              <button type="button" disabled={busy === p.id} onClick={() => buy(p.id)} data-testid={`buy-pack-${p.id}`}
                className="btn-3d mt-3 !min-h-[48px] !text-base !px-4 self-start disabled:opacity-60" style={{ "--c": "#8A3FE0" }}>
                {t("shop.buy")} · {euro(p.price)}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <Link to="/mon-lien" className="mt-4 inline-block text-sm font-semibold underline" data-testid="shop-lost-link">{t("shop.lostLink")}</Link>
    </section>
  );
};

export const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email, consent }); setDone(true); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Adresse e-mail invalide"); }
  };
  return (
    <section className="cloud-card mt-8 p-6 sm:p-8" data-testid="parents-newsletter-section">
      <h2 className="flex items-center gap-2 text-2xl font-extrabold"><Mail className="w-7 h-7 text-[#FF5FA8]" /> {t("newsletter.title")}</h2>
      <p className="mt-1 opacity-80">{t("newsletter.hint")}</p>
      {done ? <p className="mt-4 font-bold text-[#3a9a10]" data-testid="newsletter-done">{t("newsletter.done")}</p> : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("newsletter.email")}
            aria-label={t("newsletter.email")} className={inputCls} data-testid="newsletter-email-input" />
          <label className="flex items-start gap-3 text-base">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-6 w-6 shrink-0 accent-[#38B6FF]" data-testid="newsletter-consent-checkbox" />
            <span>{t("newsletter.consent")} <Link to="/confidentialite" className="underline">{t("legal.privacy")}</Link></span>
          </label>
          <button type="submit" disabled={!consent} className="btn-3d w-full disabled:opacity-50" style={{ "--c": "#FF5FA8" }} data-testid="newsletter-submit-btn">{t("newsletter.submit")}</button>
        </form>
      )}
    </section>
  );
};

export const ContactForm = () => {
  const [f, setF] = useState({ name: "", email: "", message: "", website: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", f); setDone(true); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Vérifiez le formulaire"); }
    finally { setBusy(false); }
  };
  return (
    <section className="cloud-card mt-8 p-6 sm:p-8" data-testid="parents-contact-section">
      <h2 className="flex items-center gap-2 text-2xl font-extrabold"><Send className="w-7 h-7 text-[#38B6FF]" /> {t("contact.title")}</h2>
      {done ? <p className="mt-4 font-bold text-[#3a9a10]" data-testid="contact-done">{t("contact.done")}</p> : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <input required maxLength={100} value={f.name} onChange={set("name")} placeholder={t("contact.name")} aria-label={t("contact.name")} className={inputCls} data-testid="contact-name-input" />
          <input type="email" required value={f.email} onChange={set("email")} placeholder={t("contact.email")} aria-label={t("contact.email")} className={inputCls} data-testid="contact-email-input" />
          <textarea required minLength={5} maxLength={3000} rows={5} value={f.message} onChange={set("message")} placeholder={t("contact.message")} aria-label={t("contact.message")} className={inputCls} data-testid="contact-message-input" />
          <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
          <p className="text-sm opacity-70">{t("contact.note")} <Link to="/confidentialite" className="underline">{t("legal.privacy")}</Link></p>
          <button type="submit" disabled={busy} className="btn-3d w-full disabled:opacity-60" style={{ "--c": "#38B6FF" }} data-testid="contact-submit-btn">{t("contact.submit")}</button>
        </form>
      )}
    </section>
  );
};
